import { useAppDispatch, useAppSelector } from 'appState/store'
import AudioNotificationSrc from 'assets/audio/clear-announce-tone.wav'

import { ReusableModal } from 'components/Modals/ReusableModal'
import { callsSliceLiveCallInitialState, updateLiveCall } from 'features/calls/callsSlice'
import { softphone } from 'features/calls/utils/softphone'
import { useClaim } from 'features/claims/claimsHooks'
import { getClaimCallPath } from 'features/claims/utils/claimsPathUtils'
import { useEffect, useState } from 'react'
import { FaHeadset, FaMicrophone, FaPhone } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { notificator } from 'utils/notificator/notificator'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'

export const ClaimCallJobPayorAgentReadyModal = () => {
  // global state
  const { isActivePayorAgentReady, activeCallJobId, activeClaimId } = useAppSelector(
    (state) => state.calls.talkToPayorRepLiveCall,
  )
  // utils
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { data: claim } = useClaim(activeClaimId)

  const redirectToLiveCalls = (jobId: string) => {
    if (!activeClaimId) {
      console.warn('Missing required route parameters for live call navigation')
      // Fallback navigation or stay on current route
      return
    }
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallPath(tenantId, activeClaimId, jobId))
  }

  // local state
  const [show, setShow] = useState(false)

  // effects
  useEffect(() => {
    if (isActivePayorAgentReady && !show) {
      setShow(true)
    }
  }, [isActivePayorAgentReady])

  useEffect(() => {
    if (show && activeCallJobId) {
      notificator.planAgentReadyNotification(() => {
        if (!show) return
        setShow(false)
        softphone.takeOver()
        dispatch(updateLiveCall({ isActivePayorAgentReady: false }))
        if (activeCallJobId) {
          redirectToLiveCalls(activeCallJobId)
        }
      })
    }
  }, [show, activeCallJobId])

  useEffect(() => {
    let notification: null | HTMLAudioElement = null
    if (show) {
      notification = new Audio(AudioNotificationSrc)
      notification.play()

      setTimeout(() => {
        if (notification) {
          notification.currentTime = 0
          notification.play()
        }
      }, 4000)
    }
  }, [show])

  // handlers
  const handleHide = () => {
    softphone.sendAudioMessage('I am sorry, I am not able to talk right now.')
    softphone.hangUp()
    dispatch(updateLiveCall(callsSliceLiveCallInitialState))
    setShow(false)
  }

  const handleTakeOver = () => {
    softphone.takeOver()
    setShow(false)
    dispatch(updateLiveCall({ isActivePayorAgentReady: false }))
    redirectToLiveCalls(activeCallJobId)
  }

  return (
    <>
      <audio src={AudioNotificationSrc} id="notification"></audio>
      <ReusableModal show={show} setShow={setShow} maxWidth={'431px'}>
        <Div className="user-select-none">
          <div className="section-title">
            <FaHeadset />
            <h2 className="h1">{claim?.insurerName ? claim?.insurerName : 'Unknown'} Payer Agent is Ready!</h2>
          </div>

          <div className="section-actions">
            <Button variant="danger" radius="rounded" onClick={handleHide}>
              <FaPhone /> End Call
            </Button>

            <div>
              <Button variant="success" radius="rounded" className="w-100" onClick={handleTakeOver}>
                <FaMicrophone /> Takeover
              </Button>
              <small role="label">This will unmute your mic and the call audio.</small>
            </div>
          </div>
        </Div>
      </ReusableModal>
    </>
  )
}

const Div = styled.div`
  padding: 1.5rem;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.info.lightest};
  color: ${({ theme }) => theme.colors.primary.darker};

  > .section-title {
    svg {
      width: 3rem;
      height: 3rem;
      margin-bottom: 1.25rem;
    }

    h2 {
      font-weight: ${({ theme }) => theme.fontWeight.bold};
    }
  }

  > .section-actions {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 1.5rem;

    > button:first-child svg {
      transform: rotate(-135deg);
    }

    > div:last-child {
      small {
        position: relative;
        top: -0.25rem;
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        ${({ theme }) => theme.typography.xxs.normal};
        line-height: 160%;
      }
    }
  }
`
